/**
 * Battery health data for a plant: latest snapshot, trend, alerts.
 * Authority: docs/implementation/BATTERY_HEALTH_TRACKING.md
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { computeBatteryHealthScore } from "@/lib/battery-health-score";
import { getWarrantyThresholds } from "@/lib/battery-degradation";

export interface BatteryHealthSnapshot {
  id: string;
  plant_id: string;
  property_id: string;
  snapshot_date: string;
  configured_capacity_kwh: number;
  computed_capacity_kwh: number | null;
  usable_capacity_kwh: number | null;
  total_degradation_percent: number | null;
  cycles_counted: number;
  estimated_historical_cycles: number | null;
  estimated_total_cycles: number | null;
  soc_min: number | null;
  soc_max: number | null;
  charge_kwh: number | null;
  discharge_kwh: number | null;
  baseline_type: string;
  created_at: string;
}

export interface BatteryHealthAlert {
  id: string;
  plant_id: string;
  alert_type: string;
  severity: string;
  message: string;
  detected_at: string;
  resolved_at: string | null;
}

const NINETY_DAYS_AGO = new Date();
NINETY_DAYS_AGO.setDate(NINETY_DAYS_AGO.getDate() - 90);

export function useBatteryHealth(plantId: string | undefined) {
  const latestSnapshot = useQuery({
    queryKey: ["battery-health", "latest", plantId],
    queryFn: async (): Promise<BatteryHealthSnapshot | null> => {
      if (!plantId) return null;
      const { data, error } = await supabase
        .from("battery_health_snapshots")
        .select("*")
        .eq("plant_id", plantId)
        .order("snapshot_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as BatteryHealthSnapshot | null;
    },
    enabled: Boolean(plantId),
  });

  const trendSnapshots = useQuery({
    queryKey: ["battery-health", "trend", plantId],
    queryFn: async (): Promise<BatteryHealthSnapshot[]> => {
      if (!plantId) return [];
      const { data, error } = await supabase
        .from("battery_health_snapshots")
        .select("*")
        .eq("plant_id", plantId)
        .gte("snapshot_date", NINETY_DAYS_AGO.toISOString().slice(0, 10))
        .order("snapshot_date", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BatteryHealthSnapshot[];
    },
    enabled: Boolean(plantId),
  });

  const activeAlerts = useQuery({
    queryKey: ["battery-health", "alerts", plantId],
    queryFn: async (): Promise<BatteryHealthAlert[]> => {
      if (!plantId) return [];
      const { data, error } = await supabase
        .from("battery_health_alerts")
        .select("id, plant_id, alert_type, severity, message, detected_at, resolved_at")
        .eq("plant_id", plantId)
        .is("resolved_at", null)
        .order("detected_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BatteryHealthAlert[];
    },
    enabled: Boolean(plantId),
  });

  const plant = useQuery({
    queryKey: ["plant", plantId],
    queryFn: async () => {
      if (!plantId) return null;
      const { data, error } = await supabase
        .from("plants")
        .select("id, property_id, display_name, battery_kwh_configured, install_date, monitoring_start_date, warranty_years, warranty_cycles, warranty_capacity_percent")
        .eq("id", plantId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: Boolean(plantId),
  });

  const snapshot = latestSnapshot.data ?? null;
  const installDate = plant.data?.install_date ? new Date(plant.data.install_date) : null;
  const monitoringStart = plant.data?.monitoring_start_date ? new Date(plant.data.monitoring_start_date) : null;
  const ageYears = installDate ? (Date.now() - installDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000) : null;
  const degradationRatePerYear =
    snapshot?.total_degradation_percent != null && ageYears != null && ageYears > 0
      ? snapshot.total_degradation_percent / ageYears
      : null;

  const healthScore = snapshot
    ? computeBatteryHealthScore({
        totalDegradationPercent: snapshot.total_degradation_percent,
        ageYears,
        degradationRatePerYear,
        cyclesCounted: snapshot.cycles_counted ?? 0,
        estimatedTotalCycles: snapshot.estimated_total_cycles ?? null,
      })
    : null;

  const warranty = plant.data ? getWarrantyThresholds(plant.data) : null;
  const thresholdKwh =
    warranty != null && plant.data?.battery_kwh_configured != null
      ? (Number(plant.data.battery_kwh_configured) * warranty.warrantyCapacityPercent) / 100
      : null;

  return {
    plant: plant.data,
    snapshot,
    trendSnapshots: trendSnapshots.data ?? [],
    activeAlerts: activeAlerts.data ?? [],
    healthScore,
    warranty,
    thresholdKwh,
    ageYears: ageYears != null ? Math.round(ageYears * 10) / 10 : null,
    loading: latestSnapshot.isLoading || trendSnapshots.isLoading || plant.isLoading,
    error: latestSnapshot.error ?? trendSnapshots.error ?? plant.error,
    refetch: () => {
      latestSnapshot.refetch();
      trendSnapshots.refetch();
      activeAlerts.refetch();
      plant.refetch();
    },
  };
}
